import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseCircleOutlined,
	DollarOutlined,
	DownloadOutlined,
	EditOutlined,
	InboxOutlined,
	UploadOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Card,
	Col,
	Modal,
	Result,
	Row,
	Space,
	Steps,
	Table,
	Tooltip,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import { openingBalanceApi } from 'Api/openingBalance';
import { Loader } from 'components/Global';
import Ellipse from 'components/Global/Ellipse';
import { useCallback, useMemo, useState } from 'react';
import { DeleteActionSvg } from 'utils/svgs';
import { convertArrayToCSV, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';
const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const steps = [
	{
		title: 'Upload CSV/Excel',
	},
	{
		title: 'Validation Results',
	},
	{
		title: 'Correct Data',
	},
	{
		title: 'Confirmation',
	},
];
const OpeningBalanceImport = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);

	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const [validationResults, setValidationResults] = useState([]);

	const [finalData, setFinalData] = useState([]);

	const [isErrorModal, setIsErrorModal] = useState(false);
	const [errorData, setErrorData] = useState<any>(null);

	const [currentStep, setCurrentStep] = useState(0);
	const [isImportLoading, setIsImportLoading] = useState(false);

	const [NrcUpdateObject, setNrcUpdateObject] = useState<any>(null);

	const isRecordUpdatable = (record: any) => {
		if (!NrcUpdateObject || !record['NRC No']) return false;

		// Find the corresponding entry in NrcUpdateObject
		const matchingEntry = NrcUpdateObject.find(
			(item: any) => Object.keys(item)[0] === record['NRC No']
		);

		return matchingEntry && matchingEntry[record['NRC No']] === true;
	};
	const handleModalClose = () => {
		setIsModalVisible(false);
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const handleImportMore = () => {
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const next = async () => {
		if (currentStep === 1) {
			const data = validationResults.filter((item: any) => item._isValid);
			setFinalData(data);
		}
		if (currentStep === 2) {
			await importSalaryDetails(finalData);
		} else {
			setCurrentStep((prev: any) => {
				return Math.min(prev + 1, steps.length - 1);
			});
		}
	};

	const prev = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
		if (currentStep === 1) {
			setFileList([]);
		}
	};

	const getClassName = (isValid: boolean) => {
		if (!isValid) {
			return 'required-color';
		} else {
			return '';
		}
	};

	const handleDelete = useCallback(
		(index: number) => {
			// Use slicing for immutable updates (more efficient than filter for large arrays)
			const updatedResults = [
				...validationResults.slice(0, index),
				...validationResults.slice(index + 1),
			];
			setValidationResults(updatedResults);

			if (currentStep === 2) {
				const updatedFinalData = [
					...finalData.slice(0, index),
					...finalData.slice(index + 1),
				];
				setFinalData(updatedFinalData);
			}

			toastText('Record deleted Successfully', 'success');
		},
		[validationResults, finalData, currentStep]
	);

	const columns: any = useMemo(() => {
		return [
			{
				title: 'Status',
				key: '_validationStatus',
				className: 'bg-white',
				width: 80,
				render: (_: any, record: any) =>
					record._isValid ? (
						<div
							className="text-green-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'green',
							}}
						>
							<CheckCircleOutlined />
						</div>
					) : (
						<div
							className="text-red-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'red',
							}}
						>
							<CloseCircleOutlined />
						</div>
					),
			},
			{
				title: 'Validation',
				key: '_errors',
				className: 'bg-white',
				width: 105,
				render: (_: any, record: any) => {
					return !record._isValid ? (
						<p
							onClick={() => {
								setIsErrorModal(true);
								setErrorData(record);
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'orange',
							}}
							className={`pointer ${getClassName(
								record._isValid
							)}`}
						>
							<WarningOutlined />
						</p>
					) : null;
				},
			},
			{
				title: 'Action',
				key: 'action',
				className: 'bg-white',
				width: 80,
				fixed: 'left',
				render: (_: any, record: any, index: number) => {
					return (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<Tooltip title="Delete">
								<p
									className="cursor-pointer"
									onClick={() => {
										handleDelete(index);
									}}
								>
									<DeleteActionSvg />
								</p>
							</Tooltip>
						</div>
					);
				},
			},
			...Object.keys(validationResults[0] || {})
				.filter((key) => !key.startsWith('_'))
				.map((key) => ({
					title: key,
					dataIndex: key,
					key: key,
					width: 150,
					ellipsis: true, // Truncate if text overflows
					className: 'bg-table bg-white',
					...(key === 'First Name' && {
						fixed: 'left',
						width: 180,
					}),
					render: (text: string, record: any) => {
						// Check if this is the First Name column and if the NRC is marked for update
						const isFirstNameWithEdit =
							key === 'First Name' && isRecordUpdatable(record);
						if (key === 'Add to Next Payroll') {
							const addToPayroll = text?.toString().toLowerCase();
							const isYes =
								addToPayroll === 'yes' ||
								addToPayroll === 'true' ||
								addToPayroll === '1';

							return (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}
								>
									{isYes ? 'Yes' : 'No'}
								</div>
							);
						}

						return (
							<div
								style={{
									minWidth: 150,
									display: 'flex',
									alignItems: 'center',
								}}
								className={`${getClassName(record._isValid)}`}
							>
								<Ellipse
									maxLength={20}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={text}
								/>

								{isFirstNameWithEdit && (
									<Tooltip title="This record will be updated due to having existing opening balance">
										<EditOutlined
											style={{
												marginLeft: 10,
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}
							</div>
						);
					},
				})),
		];
	}, [handleDelete, validationResults]);

	const downloadCsv = async () => {
		try {
			const response = await openingBalanceApi.downloadCsvTemplate();
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'text/csv',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `opening-balance.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		}
	};

	const handleUpload = async (file: any) => {
		try {
			setIsUploadLoading(true);

			// Detect file type
			const fileExtension = file.name
				.substring(file.name.lastIndexOf('.'))
				.toLowerCase();
			const mimeType = file.type;

			const isExcel =
				fileExtension === '.xlsx' ||
				fileExtension === '.xls' ||
				mimeType ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				mimeType === 'application/vnd.ms-excel';

			if (isExcel) {
				// For Excel: Skip frontend validation, let backend handle it
				let formData = new FormData();
				formData.append('documentName', 'opening_balance_csv');
				formData.append('file', file);

				const validatedData: any =
					await openingBalanceApi.validateOpeningBalance(formData);
				const apiResponseData = validatedData.data.data;
				const mappedNrcData = apiResponseData.map(
					(item: Record<string, any>) => {
						return {
							[item['NRC No']]: item.isUpdated,
						};
					}
				);
				setNrcUpdateObject(mappedNrcData);

				const cleanedData = apiResponseData.map(
					(item: Record<string, any>) => {
						return Object.fromEntries(
							Object.entries(item).filter(([key]) => {
								return key !== 'isUpdated';
							})
						);
					}
				);

				setValidationResults(cleanedData);
				setIsModalVisible(true);
				setCurrentStep(1);
			} else {
				// For CSV: Keep existing frontend header validation
				const reader = new FileReader();
				reader.onload = async (e: any) => {
					try {
						const csvText = e.target.result;
						const [headerLine] = csvText.split('\n');
						const uploadedColumns = headerLine
							.split(',')
							.map((col: any) =>
								col.trim().replace(/^"(.*)"$/, '$1')
							);

						// ✅ 2. Define expected columns
						const jsonData = {
							'First Name*': 'John',
							'Last Name*': 'Deo',
							'NRC No*': '755917/50/5',
							'Balance as of Date*': '25/07/2025',
							'Gross Pay YTD (ZMW)*': '15000',
							'PAYE YTD (ZMW)*': '2500',
							'NAPSA YTD (ZMW)*': '1500',
							'NHIMA YTD (ZMW)*': '2500',
							'Gratuity Pay (ZMW)*': '2600',
							'Total Outstanding Loan Amount (ZMW)*': '10000',
							'Remaining Installments*': '5',
							'Leave Encashment Amount*': '2500',
							'Number Of Leave Days': 0,
							'Add to Next Payroll': false,
						};

						const requiredColumns = Object.keys(jsonData);

						// ✅ 3. Validate headers
						const missingColumns = requiredColumns.filter(
							(col) => !uploadedColumns.includes(col)
						);
						if (missingColumns.length > 0) {
							let message =
								'Please Download the CSV/Excel Template to ensure correct format:\n';

							if (missingColumns.length > 0) {
								const displayMissing = missingColumns.slice(
									0,
									3
								);
								message += `Missing: ${displayMissing.join(
									', '
								)}`;
								if (missingColumns.length > 3) {
									message += ` and ${
										missingColumns.length - 3
									} more`;
								}
							}

							toastText(message, 'error');
							setIsUploadLoading(false);
							setFileList([]);
							setCurrentStep(0);
							return;
						}
						let formData = new FormData();
						formData.append('documentName', 'opening_balance_csv');
						formData.append('file', file);

						const validatedData: any =
							await openingBalanceApi.validateOpeningBalance(
								formData
							);
						const apiResponseData = validatedData.data.data;
						const mappedNrcData = apiResponseData.map(
							(item: Record<string, any>) => {
								return {
									[item['NRC No']]: item.isUpdated,
								};
							}
						);
						setNrcUpdateObject(mappedNrcData);

						const cleanedData = apiResponseData.map(
							(item: Record<string, any>) => {
								return Object.fromEntries(
									Object.entries(item).filter(([key]) => {
										return key !== 'isUpdated';
									})
								);
							}
						);

						setValidationResults(cleanedData);
						setIsModalVisible(true);
						setCurrentStep(1);
					} catch (error: any) {
						let message =
							error?.response?.data?.message ||
							`Failed to process file. Please try again`;
						toastText(message, 'error');
					} finally {
						setIsUploadLoading(false);
					}
				};

				reader.onerror = () => {
					toastText('File reading failed', 'error');
					setIsUploadLoading(false);
				};

				reader.readAsText(file);
				return false;
			}
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				`Failed to process file. Please try again`;
			toastText(message, 'error');
			setIsUploadLoading(false);
		} finally {
			setIsUploadLoading(false);
		}

		return false;
	};

	const importSalaryDetails = async (data: any) => {
		try {
			setIsImportLoading(true);

			const csvString = convertArrayToCSV(data);

			const blob = new Blob([csvString], { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', blob, 'data.csv');

			const response = await openingBalanceApi.importOpeningBalanceCsv(
				formData
			);
			toastText(response.data.message, 'success');
			setCurrentStep(3);
		} catch (error: any) {
			let errorMessage =
				error?.response?.data?.message ||
				'Something went wrong please while importing opening balance details';
			toastText(errorMessage, 'error');
		} finally {
			setIsImportLoading(false);
		}
	};

	return (
		<div className={styles.container}>
			{/* Header Section */}
			<Card>
				<div className={styles.headerCard}>
					<Title level={3}>
						<DollarOutlined className={styles.headerIcon} />
						Opening Balance Management
					</Title>
					<Text className={styles.subtitle}>
						Upload CSV or Excel files to add opening balance for
						existing employees for current year
					</Text>
				</div>

				<Row gutter={[24, 24]} justify="center">
					<Col xs={24} sm={12} md={8}>
						<Card className={styles.cardBox}>
							<Space direction="vertical" size="small">
								<DownloadOutlined
									className={styles.downloadIcon}
								/>
								<Text strong>Download Template</Text>
								<Button type="link" onClick={downloadCsv}>
									Get CSV/Excel Template
								</Button>
							</Space>
						</Card>
					</Col>

					<Col xs={24} sm={12} md={8}>
						<Card className={styles.cardBox}>
							<Space direction="vertical" size="small">
								<UploadOutlined className={styles.uploadIcon} />
								<Text strong>Upload Opening Balance</Text>
								<Button
									type="primary"
									onClick={() => setIsModalVisible(true)}
									style={{ backgroundColor: '#584495' }}
								>
									Upload CSV/Excel File
								</Button>
							</Space>
						</Card>
					</Col>
				</Row>

				<div className={styles.footerText}>
					<Text type="secondary">
						Select "Upload CSV/Excel File" to begin the process of
						importing opening balance data for your employees. Make
						sure to download the template first to ensure proper
						formatting.
					</Text>
				</div>
			</Card>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				width="1200px"
				footer={null}
				rootClassName={styles.uploadModal}
				destroyOnClose
				closable={false}
				maskClosable={false}
				centered
			>
				<div>
					<Steps current={currentStep}>
						{steps.map((step, index) => (
							<Step key={step.title} title={step.title} />
						))}
					</Steps>

					<div className={styles.stepBox}>
						{currentStep === 0 && (
							<div className={styles.documentUpload}>
								{isUploadLoading ? (
									<Loader />
								) : (
									<Dragger
										name="file"
										multiple={false}
										accept=".csv,.xlsx,.xls"
										fileList={fileList}
										beforeUpload={(file) => {
											const fileExtension = file.name
												.substring(
													file.name.lastIndexOf('.')
												)
												.toLowerCase();
											const mimeType = file.type;

											const isCSVOrExcel =
												fileExtension === '.csv' ||
												fileExtension === '.xlsx' ||
												fileExtension === '.xls' ||
												mimeType === 'text/csv' ||
												mimeType ===
													'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
												mimeType ===
													'application/vnd.ms-excel';

											if (!isCSVOrExcel) {
												toastText(
													'Invalid file format. Please upload CSV or Excel file',
													'error'
												);
												return Upload.LIST_IGNORE;
											}

											handleUpload(file);
											return false;
										}}
										onChange={(info) => {
											let newFileList = [
												...info.fileList,
											];
											newFileList = newFileList.slice(-1);
											setFileList(newFileList);
										}}
									>
										<p className="ant-upload-drag-icon">
											<InboxOutlined />
										</p>
										<p className="ant-upload-text">
											Click or drag CSV/Excel file to this
											area to upload
										</p>
										<p className="ant-upload-hint">
											Support for a single CSV or Excel
											file upload (up to{' '}
											<span className={styles.uploadHint}>
												500 records
											</span>
											)
										</p>
									</Dragger>
								)}
							</div>
						)}

						{currentStep === 1 && (
							<div className="validation-results">
								<Table
									columns={columns}
									dataSource={validationResults ?? []}
									scroll={{
										x: 'max-content',
										y: 'calc(100vh - 400px)',
									}}
									pagination={false}
									size="middle"
									bordered
									tableLayout="fixed"
									footer={() => (
										<div className={styles.importFooter}>
											Total No. of Employees :{' '}
											{validationResults?.length || 0}
										</div>
									)}
								/>
							</div>
						)}

						{currentStep === 2 && (
							<div className="validation-results">
								<Table
									columns={columns}
									dataSource={finalData ?? []}
									scroll={{
										x: 'max-content',
										y: 'calc(100vh - 400px)',
									}}
									pagination={false}
									loading={isImportLoading}
									size="middle"
									bordered
									tableLayout="fixed"
									footer={() => (
										<div className={styles.importFooter}>
											Total No. of Employees :{' '}
											{finalData?.length || 0}
										</div>
									)}
								/>
							</div>
						)}

						{currentStep === 3 && (
							<Result
								status="success"
								title="The process of importing opening balance has started."
								subTitle={`Importing ${finalData.length} opening balance record. Only ${finalData.length} is validated and imported. This may take a moment, Please wait....`}
								icon={
									<CheckCircleFilled
										style={{ color: '#584495' }}
									/>
								}
								extra={[
									<Button
										type="primary"
										key="console"
										onClick={handleImportMore}
									>
										Import More
									</Button>,
									<Button
										key="cancel"
										onClick={handleModalClose}
									>
										Close
									</Button>,
								]}
							/>
						)}
					</div>

					{currentStep !== 3 && (
						<div className={styles.modalFooter}>
							<Button onClick={handleModalClose}>Cancel</Button>
							{currentStep !== 0 && (
								<Button
									disabled={
										isImportLoading || isUploadLoading
									}
									onClick={prev}
									type="primary"
								>
									Previous
								</Button>
							)}
							{fileList.length > 0 && (
								<Button
									disabled={
										currentStep === steps.length - 1 ||
										isImportLoading ||
										isUploadLoading ||
										(currentStep === 1 &&
											validationResults.length === 0) ||
										(currentStep === 2 &&
											finalData.length === 0)
									}
									type="primary"
									onClick={next}
								>
									{currentStep === 2
										? 'Import Opening Balance'
										: 'Next'}
								</Button>
							)}
						</div>
					)}
				</div>
			</Modal>

			{errorData && isErrorModal && (
				<Modal
					open={isErrorModal}
					onCancel={() => {
						setErrorData(null);
						setIsErrorModal(false);
					}}
					destroyOnClose
					width="90%"
					rootClassName="uploadCsvModal"
					centered
					footer={null}
				>
					<ul className={styles.errorList}>
						{Object.entries(errorData._validationErrors).map(
							([field, error]: any) => (
								<li key={field}>
									<span>{field}</span>: <span>{error}</span>
								</li>
							)
						)}
					</ul>
				</Modal>
			)}
		</div>
	);
};

export default OpeningBalanceImport;
